//#region Imports
import { Box, Tab, Tabs, Typography, Button, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import Module from '../../components/Permissions/Module';
import { useNavigate, useParams } from 'react-router-dom';
import { GlobalVariables } from '../../globalVar';
import customAxios from '../../utils/customAxios';
import { getToast } from '../../utils/toastConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
//#endregion

const UserPermissions = () => {
  const navigate = useNavigate();
  //#region State and hooks
  const { userId } = useParams();
  const [user, setUser] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState(0);
  const [userPermsData, setUserPermsData] = useState([]);
  const [activePermissions, setActivePermissions] = useState([]);
  // Add state to track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  //#endregion

  // #region API Endpoints
  const USER_PERMISSIONS_ENDPOINT = `${GlobalVariables.getURL()}permissions/users/${userId}`;
  const UPDATE_PERMISSIONS_ENDPOINT = `${GlobalVariables.getURL()}permissions/users/${userId}/update`;
  const USER_DETAILS_ENDPOINT = `${GlobalVariables.getURL()}autofillUser/${userId}`;
  // #endregion

  //#region API Calls
  const getUserPermissions = async () => {
    setIsLoading(true);

    try {
      const response = await customAxios.get(USER_PERMISSIONS_ENDPOINT, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });
      setUserPermsData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        title: 'Error al obtener permisos',
        description:
          error.response?.data?.message ||
          'Ocurrió un error al cargar los permisos del rol',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserPermissions = async () => {
    setIsLoading(true);

    const permissionsData = preparePermissionsForSubmission();

    try {
      await customAxios.patch(UPDATE_PERMISSIONS_ENDPOINT, permissionsData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      // Reset unsavedChanges state
      setHasUnsavedChanges(false);
      // Show success message
      getToast({
        status: 'success',
        title: 'Permisos actualizados correctamente',
      });
    } catch (error) {
      console.error('Error updating role permissions:', error);
      // Show error message
      getToast({
        status: 'error',
        title: 'Error al actualizar permisos',
        description:
          error.response?.data?.message || 'Ocurrió un error inesperado',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getUserDetails = async () => {
    try {
      const response = await customAxios.get(USER_DETAILS_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setUser(response.data.user_info);
    } catch (error) {
      console.log(error);
      setUser({});
    }
  };

  //#endregion

  const handlePermissionChange = useCallback(
    (permissionId, isActive) => {
      setActivePermissions((prevPermissions) => {
        const updatedPermissions = isActive
          ? [...new Set([...prevPermissions, permissionId])]
          : prevPermissions.filter((id) => id !== permissionId);

        // Update rolePermsData to reflect changes for the active module only
        setUserPermsData((prevUserPermsData) => {
          return prevUserPermsData.map((module, index) => {
            if (index !== value) return module; // Only update the currently active module

            // Modify only the permissions of the active module
            return {
              ...module,
              permissions: module.permissions.map((perm) =>
                perm.permission_id === permissionId
                  ? { ...perm, has_permission: isActive ? 1 : 0 }
                  : perm
              ),
            };
          });
        });

        // Mark as having unsaved changes
        setHasUnsavedChanges(true);

        return updatedPermissions;
      });
    },
    [value]
  );

  const preparePermissionsForSubmission = () => {
    return { permissions: activePermissions };
  };

  //#region Effects
  useEffect(() => {
    getUserPermissions();
    getUserDetails();
  }, []);

  useEffect(() => {
    if (userPermsData.length > 0) {
      const initialActivePermissions = [];
      userPermsData.forEach((module) => {
        module.permissions.forEach((perm) => {
          if (perm.has_permission === 1) {
            initialActivePermissions.push(perm.permission_id);
          }
        });
      });
      setActivePermissions(initialActivePermissions);
    }
  }, [userPermsData]);

  //#endregion

  //#region Event handlers
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  //#endregion

  const UserDetailsHeader = () => {
    return (
      <Box pt={0} pb={2} pl={1} pr={2} borderBottom={1} borderColor='grey.300'>
        <Grid container alignItems='stretch' justifyContent='space-between'>
          <Grid item xs={12} pt={0}>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <Grid item xs={9}>
                <Typography variant='h4'>
                  {user && (
                    <>
                      <span>{userId} | </span>
                      <span>
                        {user.name} {user.last_name}
                      </span>
                    </>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3} container justifyContent='flex-end'>
                <Button
                  variant='contained'
                  onClick={updateUserPermissions}
                  disabled={isLoading}
                >
                  Actualizar permisos
                </Button>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', width: '100%', padding: 3, overflowY: 'auto' }}>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <Button
          sx={{ display: 'flex' }}
          onClick={() => {
            if (hasUnsavedChanges) {
              getToast({
                status: 'info',
                title: 'Guarde los cambios antes de salir.',
                duration: 4,
              });
              return; // Prevent navigation
            }
            navigate(-1); // Only navigate if no unsaved changes
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ marginRight: '5%' }} />
          Volver
        </Button>
        <UserDetailsHeader />
      </Box>

      {/* Tabs Section */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant='scrollable'
          scrollButtons='auto'
        >
          {userPermsData.map((module, index) => (
            <Tab label={module.module_name} key={index} />
          ))}
        </Tabs>
      </Box>

      {/* Current tab description */}
      <Box sx={{ mb: 2 }}>
        <Typography variant='body1'>
          {userPermsData.length > 0 &&
          value >= 0 &&
          value < userPermsData.length ? (
            <Box>
              <Typography variant='h5' component='h2' sx={{ mb: 2 }}>
                {userPermsData[value].module_name}
              </Typography>
              <Typography variant='body1' paragraph>
                {userPermsData[value].module_description}
              </Typography>
            </Box>
          ) : (
            'Seleccione un módulo para ver sus permisos'
          )}
        </Typography>
      </Box>

      {/* Permissions display section */}
      <Box
        sx={{
          height: '85%',
          width: '100%',
          overflowY: 'auto',
          padding: 3,
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f5f5f5',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            transition: 'box-shadow 0.3s ease-in-out',
          },
        }}
      >
        {userPermsData.length !== 0 && (
          <Module
            module={userPermsData[value]}
            onPermissionChange={handlePermissionChange}
          />
        )}
      </Box>
    </Box>
  );
};

export default UserPermissions;
